import {React, _, observer, useLocalStore, observable, action, computed, classNames, toJS, trace, pluralize, useAsObservableSource, useEffect, Popover2} from '@api'
import {Icon, NumericInput, Popover, TextArea} from '@blueprintjs/core'
import {useCatalogItemSidebarDetailsStore, CatalogItemSidebarDetailsStore, VipLocalStore, useVipLocalStore, useStores} from '@modules'
import {removeUndefinedsNested} from '@utils'
import {autorun} from 'mobx'
import {Fragment, useContext} from 'react'
import * as css from './BillOfMaterialsDetails.css'
import {BillOfMaterialsConfig, BOMSpecConfig, ICatalogFieldConfig, ui, BillOfMaterialsRollupConfig, nav, useCatalog, site, onSiteError, StoresInstance} from '@stores'
import {BOMSpecUsages, SidebarSpecification, BOMRollups} from '@lazy'

export type IRollupResults = Array <{def: BillOfMaterialsRollupConfig, result? : number, missingFieldValues?: string[], fieldValues?, quantity}>

export interface IBomRollupUsage {
  _id: string
  _source?: any
  rollups: IRollupResults
}

export interface BillOfMaterialsData {
  field: string,
  quantity: number,
  comments?: string
}

class BillOfMaterialsDetailsStore {
  constructor(public props: { detailsStore: CatalogItemSidebarDetailsStore; config: BillOfMaterialsConfig }) {
    if (_.isEmpty(this.props.config?.specs)) {
      throw new Error(`Bill of Materials requires list of specs`)
    }

    // Setup any missing associations (quantity <-> spec for the catalog item)
    if (!this.data.billOfMaterials) {
      this.data.billOfMaterials = []
    }

    const {data, props: {detailsStore, config: {specs}}, bomDataDict} = this
    specs.forEach(({field}) => {
      if (!bomDataDict[field]) {
        data.billOfMaterials.push({field, quantity: 1})
      }
    })
    detailsStore.isDirty && detailsStore.mutationSaveCatalogItemDetails()
  }

  get data(): { _id: string, billOfMaterials?: BillOfMaterialsData[] } { return this.props.detailsStore.data}

  get bomDataDict(): { [attribute: string]: BillOfMaterialsData } {
    return _.keyBy(this.bomData, d => d.field)
  }

  @computed get bomData(): Array<BillOfMaterialsData> {
    return this.props.detailsStore.readFieldData('billOfMaterials')?.val ?? []
  }

  @computed get localFields(): Array<string> {
    const {props: {config: {specs}}} = this
    return _.chain(specs).flatMap(s => s.rollups?.map(r => r.localFields)).flatten().compact().uniq().value()
  }

  @observable specRowStores: { [field: string]: BillOfMaterialsSpecRowStore } = {}
}

export const BillOfMaterialsDetailsStoreContext = React.createContext<BillOfMaterialsDetailsStore>(undefined)
export const useBillOfMaterialsDetailsStore = () => useContext(BillOfMaterialsDetailsStoreContext)

const LocalFieldsGrid = observer(function LocalFieldsGrid() {
  const {localFields} = useBillOfMaterialsDetailsStore()
  return (
    <div className={css.localFieldsGrid}>
      {localFields.map(field => <LocalField key={field} field={field}/>)}
    </div>
  )
})

class LocalFieldStore extends VipLocalStore {
  constructor(stores: StoresInstance,public field: string, public bomStore: BillOfMaterialsDetailsStore) {
    super(stores)
  }

  @computed get value() {
    const {field, bomStore: {props: {detailsStore: {data}}}} = this
    return _.get(data, field) ?? undefined
  }

  @action onValueChange = v => {
    const {field, bomStore: {props: {detailsStore: {data}}}} = this
    return _.set(data, field, v)
  }

  @computed get catalogField() { return this.catalog.fieldByName(this.field)}
}

const LocalField = observer<{field: string}>(function LocalField({field}) {
  const bomStore = useBillOfMaterialsDetailsStore(),
    {strings, value, onValueChange, catalogField, catalog} = useVipLocalStore((ws) => new LocalFieldStore(ws, field, bomStore))

  return (
    <div className={css.localField}>
      <label>{catalog.labelForField(catalogField)}</label>
      <NumericInput title={field}
                    allowNumericCharactersOnly
                    selectAllOnFocus
                    min={0}
                    value={_.isNaN(value) ? 0 : value}
                    placeholder={strings.noValue}
                    onValueChange={onValueChange}/>
    </div>
  )
})

//<div className={css.localField}>
//         <NumericInput allowNumericCharactersOnly selectAllOnFocus value={quantity} onValueChange={onQuantityValueChange}/>
//       </div>)}
const ColumnHeaders = observer(function ColumnHeaders() {
  const {itemCount} = useCatalog()
  return (
    <Fragment>
      <div data-type='ColumnHeader' className={css.spec}>Specification</div>
      <div data-type='ColumnHeader' className={css.quantity}>Quantity</div>
      <div data-type='ColumnHeader' className={css.catalogItemTotals}>Product Totals</div>
      <div data-type='ColumnHeader' className={css.catalogGlobalTotals}>Seasonal Totals{false && itemCount !== undefined ? ` (${itemCount})` : ''}</div>
      <div data-type='ColumnHeader' className={css.notes}>Notes</div>
    </Fragment>
  )
})

class BillOfMaterialsSpecRowStore extends VipLocalStore {
  constructor(stores: StoresInstance,public bomStore: BillOfMaterialsDetailsStore, public fieldConfig: BOMSpecConfig) {
    super(stores)
    bomStore.specRowStores[fieldConfig.field] = this

    const {catalog, catalogField} = this

    if (!catalogField) {
      throw new Error(`${fieldConfig.field} does not exist on the catalog field list for workspace ${catalog.workspaceConfig._id}`)
    } else if (!catalogField.esIndex) {
      throw new Error(`${catalogField.field} is not associated with an esIndex - Bill of Material field must be a specification`)
    }

    const {associatedWorkspace} = this

    if (!associatedWorkspace) {
      throw new Error(`No workspace is defined with a catalog for index ${catalogField.esIndex}`)
    }

    (fieldConfig.bomFields ?? []).forEach(f => {
      if (!associatedWorkspace.fields[f]) {
        throw new Error(`The field ${f} on workspace '${associatedWorkspace.workspace.title}' is not defined`)
      }
    })

    if (!this.association) {
      this.bomStore.bomData.push({field: fieldConfig.field, quantity: 1})
    }
  }

  registerEffects() {
    const toDispose = [
      autorun(async () => {
      const {associatedSpecValue} = this

      await Promise.all([this.loadBOMFields(), this.loadGlobalTotals()])
    }, {name: `Bill of material store autoruns`})
      ]

    return () => toDispose.forEach(f => f())
  }

  @computed get associatedWorkspace() {
    const {catalogField: {esIndex}, catalog} = this
    return catalog.fieldByWorkspaceIndex[esIndex]
  }

  @computed get associatedSpecValue() {
    const {fieldConfig, bomStore: {props: {detailsStore: {readFieldData}}}} = this
    return readFieldData(fieldConfig.field)?.val
  }

  @observable loading = true

  @observable.ref bomFields: Array<{ field: ICatalogFieldConfig, value?: any }> = []

  @computed get bomFieldsByName() {
    return this.bomFields.reduce((acc, {field, value}) => _.assign(acc, {[field.field]: value}), {})
  }

  @action loadBOMFields = async () => {
    const {es, catalog, associatedSpecValue, associatedWorkspace, catalogField: {esIndex}, fieldConfig: {bomFields, field}} = this,
      id = associatedSpecValue ? catalog.idFor(associatedSpecValue) : null
    if (_.isEmpty(bomFields)) {
      console.warn(`No bill of material fields are defined on the detail view for BOM field '${field}'`)
    }
    this.loading = true

    try {
      // Query the related index for the associated BOM attributes
      const data = !associatedSpecValue ? {} : await es.get({
        index: esIndex,
        _source: bomFields,
        id
      })

      let undefinedFields: ICatalogFieldConfig[] = []
      const values = (bomFields ?? []).map(field => {
        const value = _.get(data, field),
          catalogField = associatedWorkspace.fields[field]

        value === undefined && undefinedFields.push(catalogField)
        return {field: catalogField, value: !associatedSpecValue ? null : value ?? catalogField.defaultValue, isUndefined: value === undefined}
      })

      //console.log(toJS({esIndex, id, data, values}, {recurseEverything: true}))

      if (associatedSpecValue) {
        // Add defaults for any that don't exist
        const undefinedValues = values.filter(v => v.isUndefined),
          missing = removeUndefinedsNested(
            undefinedValues.reduce((acc, {field}) => _.merge(acc, _.set({}, field.field, field.defaultValue)), {})
          )

        if (!_.isEmpty(missing)) {
          await es.updateDocument({
            index: esIndex,
            id,
            doc: {
              ...missing
            }
          })
        }
      }

      this.bomFields = values
    } catch (err) {
      onSiteError(err)
    } finally {
      this.loading = false
    }
  }

  @computed get catalogField() {
    return this.catalog.fieldByName(this.fieldConfig.field)
  }

  @computed get association() {
    const {bomStore: {bomDataDict}, fieldConfig: {field}} = this
    return bomDataDict[field]
  }

  @computed get quantity() {
    return this.association?.quantity ?? 1
  }

  @action onQuantityValueChange = (v) => {
    this.association.quantity = _.isNaN(v) ? 0 : v
  }

  labelFor = (fieldName: string) => {
    // Might be local, might be remote

    const {bomFields, catalog} = this
    const field = bomFields.find(f => f.field.field == fieldName)?.field ?? catalog._fieldByName[fieldName]
    return !field ? fieldName : catalog.labelForField(field)
  }

  calculateRollup = ({rollupDef, quantity, data}: { rollupDef: BillOfMaterialsRollupConfig, data, quantity: number }) => {
    const {associatedSpecValue, bomFieldsByName, catalog} = this
    return catalog.calculateRollup({rollupDef, quantity, data, spec: associatedSpecValue, bomFieldsByName})
  }

  @computed get rollupResults(): IRollupResults {
    const {calculateRollup, quantity, fieldConfig: {rollups}, bomStore: {props: {detailsStore: {data}}}} = this
    return (rollups ?? []).map(rollup => calculateRollup({rollupDef: rollup, data, quantity}))
  }

  @observable.ref rollupByCatalogItem: Array<IBomRollupUsage & {_source: any}>

  @computed get allUsesOfSpec() : Array<IBomRollupUsage> {
    const {rollupByCatalogItem, rollupResults, bomStore: {data: {_id}}} = this
    return [...(rollupByCatalogItem ?? []), {_id, rollups: rollupResults.filter(r => !r.def.localOnly)}]
  }
  @computed get globalTotals() {
    const {allUsesOfSpec} = this

    // Sum the rolled up values
    const totals = allUsesOfSpec.reduce((totals, {rollups}) => {
      //console.log(toJS({totals, rollupResults}, {recurseEverything: true}))

      return rollups.filter(r => r.result !== null).reduce(
        (acc, {def, result}, i) => {
          //console.log(acc, result, def.label, acc[def.label])
          return _.assign(acc, {[def.label]: {def, i, total: (acc[def.label]?.total ?? 0) + result}})
        }, totals)
    }, {}) as { [label: string]: { def: BillOfMaterialsRollupConfig, i: number, total?: number } }

    //console.log(totals)
    const grandTotal = _.chain(totals).values().orderBy(t => t.i).value()

    return grandTotal ?? []
  }

  @action loadGlobalTotals = async () => {
    const {es, catalog: {dataMap, itemCount, index, catalogItemFilter},
      associatedSpecValue, bomStore: {localFields, data: {_id: myId}}, fieldConfig: {rollups}, calculateRollup, catalogField} = this

    if (!associatedSpecValue) { this.rollupByCatalogItem = [] } else {
      const {aggregations, hits: {hits}} = await es.search({
        index: index,
        body: {
          ..._.merge({}, catalogItemFilter.filter,
            {
              query: {
                bool: {
                  must: [
                    {exists: {field: 'billOfMaterials'}},
                    ...localFields.map(field => ({exists: {field}})),
                    {term: {[`${catalogField.field}.id`]: associatedSpecValue.id}}
                  ]
                }
              }
            }),
          size: itemCount,
          _source: _.uniq(['billOfMaterials', ...localFields, ..._.values(dataMap)])
        }
      }, {log: {query: false, result: false}})

      // Calculate BOM totals for each individual spec rollup
      this.rollupByCatalogItem = hits.map(({_id, _source: {billOfMaterials, ..._source}}) => {
        if (_id === myId) return null

        const quantity = billOfMaterials.find(bom => bom.field === catalogField.field)?.quantity
        if (quantity !== undefined) {
          //console.log(catalogField.field, billOfMaterials, _source, quantity)
          return {_id, _source, rollups: (rollups || []).filter(r => !r.localOnly).map(r => calculateRollup({rollupDef: r, data: _source, quantity}))}
        }

        return null
      }).filter(_.identity)
    }
  }

  @computed get comments() { return this.association.comments }

  @action onChangeComments = v => { this.association.comments = v}
}

export const BillOfMaterialsSpecRowStoreContext = React.createContext<BillOfMaterialsSpecRowStore>(undefined)
export const useBillOfMaterialsSpecRowStore = () => useContext(BillOfMaterialsSpecRowStoreContext)

const BillOfMaterialSpecRow = observer<{ index: number, field: BOMSpecConfig }>(function BillOfMaterialSpecRow({index, field}) {
  const bomStore = useBillOfMaterialsDetailsStore(),
    style = {gridRow: index + 2},
    store = useVipLocalStore((ws) => new BillOfMaterialsSpecRowStore(ws, bomStore, field)),
    {quantity, onQuantityValueChange, catalogField, associatedSpecValue, rollupResults, catalog} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <BillOfMaterialsSpecRowStoreContext.Provider value={store}>
      <div data-type='SpecCell' style={style} className={css.spec}>
        {false && <span className={css.specLabel}>{catalog.labelForField(catalogField)}</span>}

        <SidebarSpecification field={catalogField} data={associatedSpecValue} popoverPosition='left'/>

        {associatedSpecValue && <Fragment>
         <SpecBOMFields/>
        </Fragment>}
      </div>

      <div data-type='SpecCell' style={style} className={css.notes}>
        <SpecBOMCommentsCell />
      </div>

      <div data-type='SpecCell' style={style} className={css.quantity}>
        <NumericInput tabIndex={index} allowNumericCharactersOnly  min={0} stepSize={1} minorStepSize={.01} selectAllOnFocus value={_.isNaN(quantity) ? 0 : quantity} onValueChange={onQuantityValueChange}/>
      </div>

      <div className={css.catalogItemTotals} data-type='SpecCell' style={style}>
        <BOMRollups rollupResults={rollupResults}/>
      </div>

      <div className={css.catalogGlobalTotals} data-type='SpecCell' style={style}>
        <GlobalSpecBOMRollups/>
      </div>
    </BillOfMaterialsSpecRowStoreContext.Provider>
  )
})

export const SpecBOMCommentsCell = observer<{comments?: Array<{}>}>(function SpecBOMCommentsCell({comments}) {
  return (
    <Popover2
      className={css.commentsPopover}
      content={<SpecBOMComments/>}
      interactionKind='hover'
      position='left'
    >
      <div className={css.contents}>
        <a>{!comments?.length ? 'No' : comments.length} {pluralize('comments', comments?.length ?? 0)}</a>
        <Icon icon='annotation'/>
      </div>
    </Popover2>
  )
})

const LineItem = observer<{ label: string, value: number, precision?: number, prefix?: React.ReactNode, suffix?: React.ReactNode }>(function LineItem({label, value, precision, prefix, suffix}) {
  return (
    <div className={css.lineItem}>
      <label>{label}:</label>
      <span className={css.value}>{prefix}{value.toLocaleString('en-US', {minimumFractionDigits: precision ?? 0, maximumFractionDigits: precision ?? 0})}{suffix}</span>
    </div>
  )
})

const SpecBOMFields = observer(function SpecBOMFields() {
  const {strings, catalog, bomFields, rollupByCatalogItem} = useBillOfMaterialsSpecRowStore()

  return (
    <Fragment>
    <div className={css.bomFields}>
      {(bomFields ?? []).map(({field, value}, i) =>
        <div className={css.field} key={i} data-field={field.field} data-value={value}>
          <label>{catalog.labelForField(field)}</label>
          <span className={css.value}>{catalog.labelForFieldValue(field, value) ?? strings.emptyLabel}</span>
        </div>)}
    </div>
      <BOMSpecUsages />
    </Fragment>
  )
})


const SpecBOMComments = observer(() => {
  const store = useBillOfMaterialsSpecRowStore()
  return (
    <div className={css.commentsPopup}>
      <TextArea value='Comments' onChange={store?.onChangeComments}/>
    </div>
  )
})

export const GlobalSpecBOMRollups = observer(() => {
  const {strings, catalog: {labelFor}, globalTotals, associatedSpecValue} = useBillOfMaterialsSpecRowStore()
  return (
    <div className={css.rollups}>
      {_.isEmpty(globalTotals) ? <div className={css.noRollup}>{!associatedSpecValue ? strings.noValue : `'${labelFor(associatedSpecValue)}' is not currently in-use.`}</div>
       : <Fragment>
       {globalTotals.map(({def: {label, prefix, precision}, total}, i) => {
        //console.log({result, label, data, missingFieldValues, fieldValues, quantity})
        return (
          <div className={css.rollup} key={i}>
            <label>Total {label}</label>
            <span className={css.value}>
              {(total === null) // Did we not find some of the fields?
               ? <span>{strings.emptyLabel}</span>
               : `${prefix ?? ''}${total.toLocaleString('en-US', {minimumFractionDigits: precision ?? 0, maximumFractionDigits: precision ?? 0})}`}
            </span>
          </div>
        )
      })}
       </Fragment>}
    </div>
  )
})

const BillOfMaterialsTotalsRow = observer(() => {
  return (
    <Fragment>
      <div className={css.emptyTotal} style={{gridColumn: 1}}></div>
      <div className={css.emptyTotal} style={{gridColumn: 2}}></div>
      <RollupTotals/>
      <GlobalRollupTotals />
      <div className={css.emptyTotal} style={{gridColumn: 5}}></div>
    </Fragment>
  )
})

class RollupTotalsStore {
  constructor(public bomStore: BillOfMaterialsDetailsStore) {}

  @computed get rollupTotals() {
    const {bomStore: {specRowStores}} = this,
      totals = _.values(specRowStores).reduce((totals, {rollupResults}) => {
        //console.log(toJS({totals, rollupResults}, {recurseEverything: true}))

        return rollupResults.filter(r => r.result !== null).reduce(
          (acc, {def, result}, i) => {
            //console.log(acc, result, def.label, acc[def.label])
            return ({
              ...acc,
              [def.label]: {def, i, total: (acc[def.label]?.total ?? 0) + result}
            })
          }, totals)
      }, {}) as { [label: string]: { def: BillOfMaterialsRollupConfig, i: number, total?: number } }

    //console.log(totals)
    return _.chain(totals).values().orderBy(t => t.i).value()
  }
}

const RollupTotals = observer(() => {
  const bomStore = useBillOfMaterialsDetailsStore(),
    {rollupTotals} = useLocalStore(() => new RollupTotalsStore(bomStore)),
    {strings} = useStores()

  return (
    <div className={css.rollupTotals}>
      {rollupTotals.map(({def: {label, prefix, precision}, i, total}) => {
        return (
          <div key={i} className={css.rollup}>
            <label>{label}</label>
            <span className={css.value}>
              {(total === null) // Did we not find some of the fields?
               ? <span>{strings.emptyLabel}</span>
               : `${prefix ?? ''}${total.toLocaleString('en-US', {minimumFractionDigits: precision ?? 0, maximumFractionDigits: precision ?? 0})}`}
            </span>
          </div>
        )
      })}
    </div>
  )
})

class GlobalRollupTotalsStore {
  constructor(public bomStore: BillOfMaterialsDetailsStore) {}

  @computed get rollupTotals() {
    const {bomStore: {specRowStores}} = this,
      totals = _.values(specRowStores).reduce((totals, {globalTotals}) => {
        //console.log(toJS({totals, rollupResults}, {recurseEverything: true}))

        return globalTotals.filter(r => r.total !== null).reduce(
          (acc, {def, total}, i) => {
            //console.log(acc, result, def.label, acc[def.label])
            return ({
              ...acc,
              [def.label]: {def, i, total: (acc[def.label]?.total ?? 0) + total}
            })
          }, totals)
      }, {}) as { [label: string]: { def: BillOfMaterialsRollupConfig, i: number, total?: number } }

    //console.log(totals)
    return _.chain(totals).values().orderBy(t => t.i).value()
  }
}

const GlobalRollupTotals = observer(() => {
  const bomStore = useBillOfMaterialsDetailsStore(),
    {rollupTotals} = useLocalStore(() => new GlobalRollupTotalsStore(bomStore)),
    {strings} = useStores()

  return (
    <div className={css.rollupTotalsGlobal}>
      {rollupTotals.map(({def: {label, prefix, precision}, i, total}) => {
        return (
          <div key={i} className={css.rollup}>
            <label>Total {label}</label>
            <span className={css.value}>
              {(total === null) // Did we not find some of the fields?
               ? <span>{strings.emptyLabel}</span>
               : `${prefix ?? ''}${total.toLocaleString('en-US', {minimumFractionDigits: precision ?? 0, maximumFractionDigits: precision ?? 0})}`}
            </span>
          </div>
        )
      })}
    </div>
  )
})

export const BillOfMaterialsDetails = observer<{ className?: string, config: BillOfMaterialsConfig }>(({className, config}) => {
  const detailsStore = useCatalogItemSidebarDetailsStore(),
    obsProps = useAsObservableSource({config, detailsStore}),
    store = useLocalStore(() => new BillOfMaterialsDetailsStore(obsProps)),
    {props: {config: {specs}}, localFields} = store

  //console.log(toJS({specs, localFields}, {recurseEverything: true}))

  return (
    <BillOfMaterialsDetailsStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}
           style={{'--specs': specs.length} as any}>

        {!_.isEmpty(localFields) && <LocalFieldsGrid/>}
        <div className={css.specGrid}>
          <ColumnHeaders/>
          {specs.map((field, i) => <BillOfMaterialSpecRow key={field.field} field={field} index={i}/>)}
          {specs.length && <BillOfMaterialsTotalsRow/>}
        </div>
      </div>
    </BillOfMaterialsDetailsStoreContext.Provider>
  )
})



// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/FieldRenderers/BillOfMaterials/BillOfMaterialsDetails.tsx